export const STANDARD_ORDER_TYPES = {
  0: "Dine In",
  1: "Pick Up",
  2: "Delivery",
};

export const normalizeOrderTypeName = (name) =>
  typeof name === "string" ? name.trim().toLowerCase() : "";

export const areOrderTypeNamesEqual = (left, right) => {
  const normalizedLeft = normalizeOrderTypeName(left);
  const normalizedRight = normalizeOrderTypeName(right);

  return Boolean(normalizedLeft && normalizedLeft === normalizedRight);
};

export const getOrderTypeGroup = (name) => {
  const normalizedName = normalizeOrderTypeName(name);

  if (normalizedName.includes("deliver")) return 2;
  if (normalizedName.includes("dine")) return 0;
  return 1;
};

export const isStandardOrderType = (name) => {
  return ["dine in", "pick up", "delivery"].includes(
    normalizeOrderTypeName(name)
  );
};

export const getOrderTypeDisplayLabel = (name, group) => {
  const standardLabels = {
    0: "Dine In",
    1: "Pickup",
    2: "Delivery",
  };
  const normalizedName = normalizeOrderTypeName(name);

  if (normalizedName === "dine in") return standardLabels[0];
  if (normalizedName === "pick up") return standardLabels[1];
  if (normalizedName === "delivery") return standardLabels[2];

  const numericGroup = Number(group);
  const displayGroup = [0, 1, 2].includes(numericGroup)
    ? numericGroup
    : getOrderTypeGroup(name);

  return typeof name === "string" && name
    ? name
    : standardLabels[displayGroup];
};

export const getRenderableOrderTypes = (
  names,
  { dineIn, pickup, delivery } = {}
) => {
  if (!Array.isArray(names)) return [];

  const enabledByGroup = {
    0: Number(dineIn) === 1,
    1: Number(pickup) === 1,
    2: Number(delivery) === 1,
  };

  return names.filter(
    (name) =>
      typeof name === "string" &&
      normalizeOrderTypeName(name) &&
      enabledByGroup[getOrderTypeGroup(name)]
  );
};

export const getRenderableOrderTypeForGroup = (names, group) => {
  if (!Array.isArray(names)) return null;

  return (
    names.find((name) => getOrderTypeGroup(name) === Number(group)) || null
  );
};

const NON_SL_ASSISTANT_SERVICE_TYPE_MAP = {
  "0": { idx: 0, label: "Pickup", key: "pickup" },
  "1": { idx: 1, label: "Dine In", key: "dine in" },
  "2": { idx: 2, label: "Delivery", key: "delivery" },
  "dine-in": { idx: 1, label: "Dine In", key: "dine in" },
  "dine in": { idx: 1, label: "Dine In", key: "dine in" },
  dinein: { idx: 1, label: "Dine In", key: "dine in" },
  pickup: { idx: 0, label: "Pickup", key: "pickup" },
  "pick-up": { idx: 0, label: "Pickup", key: "pickup" },
  "pick up": { idx: 0, label: "Pickup", key: "pickup" },
  takeaway: { idx: 0, label: "Pickup", key: "pickup" },
  "to-go": { idx: 0, label: "Pickup", key: "pickup" },
  "to go": { idx: 0, label: "Pickup", key: "pickup" },
  delivery: { idx: 2, label: "Delivery", key: "delivery" },
  "room-delivery": { idx: 2, label: "Delivery", key: "delivery" },
  "room delivery": { idx: 2, label: "Delivery", key: "delivery" },
};

const SL_ASSISTANT_SERVICE_TYPE_MAP = {
  "0": { idx: 0, label: "Dine In", key: "dine in" },
  "1": { idx: 1, label: "Pickup", key: "pickup" },
  "2": { idx: 2, label: "Delivery", key: "delivery" },
  "dine-in": { idx: 0, label: "Dine In", key: "dine in" },
  "dine in": { idx: 0, label: "Dine In", key: "dine in" },
  dinein: { idx: 0, label: "Dine In", key: "dine in" },
  pickup: { idx: 1, label: "Pickup", key: "pickup" },
  "pick-up": { idx: 1, label: "Pickup", key: "pickup" },
  "pick up": { idx: 1, label: "Pickup", key: "pickup" },
  takeaway: { idx: 1, label: "Pickup", key: "pickup" },
  "to-go": { idx: 1, label: "Pickup", key: "pickup" },
  "to go": { idx: 1, label: "Pickup", key: "pickup" },
  delivery: { idx: 2, label: "Delivery", key: "delivery" },
  "room-delivery": { idx: 2, label: "Delivery", key: "delivery" },
  "room delivery": { idx: 2, label: "Delivery", key: "delivery" },
};

export const getAssistantServiceTypeMap = (isSL) =>
  isSL ? SL_ASSISTANT_SERVICE_TYPE_MAP : NON_SL_ASSISTANT_SERVICE_TYPE_MAP;

const SL_VOICE_SERVICE_OPTIONS = {
  "0": { idx: 0, label: "Dine In", key: "dine in" },
  "1": { idx: 1, label: "Pickup", key: "pickup" },
  "2": { idx: 2, label: "Delivery", key: "delivery" },
  "dine-in": { idx: 0, label: "Dine In", key: "dine in" },
  "dine in": { idx: 0, label: "Dine In", key: "dine in" },
  dinein: { idx: 0, label: "Dine In", key: "dine in" },
  pickup: { idx: 1, label: "Pickup", key: "pickup" },
  "pick-up": { idx: 1, label: "Pickup", key: "pickup" },
  "pick up": { idx: 1, label: "Pickup", key: "pickup" },
  takeaway: { idx: 1, label: "Pickup", key: "pickup" },
  "to-go": { idx: 1, label: "Pickup", key: "pickup" },
  "to go": { idx: 1, label: "Pickup", key: "pickup" },
  delivery: { idx: 2, label: "Delivery", key: "delivery" },
  "room-delivery": { idx: 2, label: "Delivery", key: "delivery" },
  "room delivery": { idx: 2, label: "Delivery", key: "delivery" },
};

export const getSLVoiceServiceOption = (value) => {
  if (value === 0 || value === 1 || value === 2) {
    return SL_VOICE_SERVICE_OPTIONS[value];
  }

  return SL_VOICE_SERVICE_OPTIONS[normalizeOrderTypeName(value)] || null;
};

export const normalizeAvailableOrderTypes = (rows) => {
  if (!Array.isArray(rows)) return [];

  const seen = new Set();
  return rows.reduce((orderTypes, row) => {
    const orderType = typeof row?.OrderType === "string" ? row.OrderType.trim() : "";
    const normalizedOrderType = normalizeOrderTypeName(orderType);

    if (orderType && !seen.has(normalizedOrderType)) {
      seen.add(normalizedOrderType);
      orderTypes.push(orderType);
    }

    return orderTypes;
  }, []);
};

export const getSLSubmittedOrderType = ({
  orderProcessingType,
  selectedOrderTypeName,
  labels,
}) => {
  if (
    typeof selectedOrderTypeName === "string" &&
    selectedOrderTypeName.trim() &&
    !isStandardOrderType(selectedOrderTypeName)
  ) {
    return selectedOrderTypeName;
  }

  return {
    0: labels.dineIn,
    1: labels.pickup,
    2: labels.delivery,
  }[orderProcessingType];
};

export const getSLWorldpayOrderType = ({
  orderProcessingType,
  selectedOrderTypeName,
}) => {
  if (
    typeof selectedOrderTypeName === "string" &&
    selectedOrderTypeName.trim() &&
    !isStandardOrderType(selectedOrderTypeName)
  ) {
    return selectedOrderTypeName;
  }

  return {
    0: "dine-in",
    1: "pickup",
    2: "delivery",
  }[orderProcessingType];
};

export const getPickupTimeLimitServiceOption = ({
  orderProcessingType,
  selectedOrderTypeName,
}) => {
  if (
    typeof selectedOrderTypeName === "string" &&
    selectedOrderTypeName.trim() &&
    !isStandardOrderType(selectedOrderTypeName)
  ) {
    return encodeURIComponent(selectedOrderTypeName);
  }

  return orderProcessingType;
};

export const formatOrderTypeFee = (fee) => {
  const numericFee = Number(fee);
  return numericFee > 0 ? ` – $${numericFee.toFixed(2)}` : "";
};
